<template>
  <Dialog
    :visible="modelValue"
    modal
    :style="{ width: '64rem' }"
    :breakpoints="{ '1199px': '85vw', '575px': '95vw' }"
    :draggable="false"
    :show-header="false"
    content-class="sa-content"
    @update:visible="handleClose"
  >
    <div class="sa-modal">
      <!-- HEADER -->
      <div class="sa-header">
        <div>
          <h3 class="sa-title">{{ t('saleAttribution.title') }}</h3>
          <div class="sa-subtitle">
            <span>{{ t('saleAttribution.saleOn', { date: formattedSaleDate }) }}</span>
            <template v-if="detail">
              <span class="sa-subtitle-sep">·</span>
              <span>{{ t('saleAttribution.order') }}</span>
              <code class="sa-order-code">#{{ detail.idShopOrder }}</code>
            </template>
          </div>
        </div>
        <button class="sa-close" @click="handleClose">✕</button>
      </div>

      <!-- LOADING -->
      <div v-if="loading" class="sa-state">
        <i class="pi pi-spin pi-spinner text-3xl tc-primary" />
      </div>

      <!-- EMPTY -->
      <div v-else-if="!detail || allTouches.length === 0" class="sa-state">
        <i class="far fa-info-circle text-3xl tc-gray-400" />
        <p class="sa-state-text">{{ t('saleAttribution.noAttribution') }}</p>
      </div>

      <!-- BODY -->
      <template v-else>
        <!-- KPI cards -->
        <div class="sa-kpis">
          <div class="sa-kpi">
            <div class="sa-kpi-label">
              <span class="sa-kpi-dot sa-kpi-dot--primary" />
              {{ t('saleAttribution.kpi.amount') }}
            </div>
            <div class="sa-kpi-value sa-kpi-value--amount">{{ formatAmount(totalAmount, currency) }}</div>
            <div class="sa-kpi-trend">{{ t('saleAttribution.kpi.amountHint') }}</div>
          </div>
          <div class="sa-kpi">
            <div class="sa-kpi-label">
              <span class="sa-kpi-dot sa-kpi-dot--astronaut" />
              {{ t('saleAttribution.kpi.touches') }}
            </div>
            <div class="sa-kpi-value">{{ allTouches.length }}</div>
            <div class="sa-kpi-trend">
              {{ t('saleAttribution.kpi.touchesBreakdown', { auto: autoTouchesCount, bulk: bulkTouchesCount }) }}
            </div>
          </div>
          <div class="sa-kpi">
            <div class="sa-kpi-label">
              <span class="sa-kpi-dot sa-kpi-dot--orange" />
              {{ t('saleAttribution.kpi.tracking') }}
            </div>
            <div class="sa-kpi-value">{{ trackedCount }} / {{ allTouches.length }}</div>
            <div class="sa-kpi-trend">
              {{ untrackedCount === 0
                ? t('saleAttribution.kpi.trackingAllOk')
                : t('saleAttribution.kpi.trackingExcluded', { n: untrackedCount }) }}
            </div>
          </div>
          <div class="sa-kpi">
            <div class="sa-kpi-label">
              <span class="sa-kpi-dot sa-kpi-dot--plum" />
              {{ t('saleAttribution.kpi.winnerLag') }}
            </div>
            <div class="sa-kpi-value">{{ winnerLagDisplay || '—' }}</div>
            <div class="sa-kpi-trend">{{ t('saleAttribution.kpi.winnerLagHint') }}</div>
          </div>
        </div>

        <!-- Table -->
        <div class="sa-table-wrap">
          <div class="sa-table-title">
            <span>{{ t('saleAttribution.tableTitle') }}</span>
            <span class="sa-table-title-hint">{{ t('saleAttribution.sortedByPriority') }}</span>
          </div>

          <table class="sa-table">
            <thead>
              <tr>
                <th style="width:48px">{{ t('saleAttribution.rank') }}</th>
                <th style="width:96px">{{ t('saleAttribution.type') }}</th>
                <th>{{ t('saleAttribution.scenario') }}</th>
                <th style="width:96px">{{ t('saleAttribution.channel') }}</th>
                <th style="width:128px">{{ t('saleAttribution.dateSend') }}</th>
                <th style="width:152px">{{ t('saleAttribution.lastInteraction') }}</th>
                <th style="width:104px">{{ t('saleAttribution.lagSinceInteraction') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="touch in allTouches"
                :key="`${touch.kind}-${touch.idSend}`"
                :class="{ 'sa-row-winner': touch.isEffectiveWinner }"
              >
                <td class="sa-rank" :class="{ 'sa-rank-top': touch.isEffectiveWinner }">#{{ touch.rank }}</td>
                <td>
                  <span class="sa-chip" :class="touch.kind === 'automation' ? 'sa-chip-auto' : 'sa-chip-bulk'">
                    {{ touch.kind === 'automation' ? t('saleAttribution.automation') : t('saleAttribution.bulk') }}
                  </span>
                </td>
                <td>
                  <div class="sa-scenario">{{ touch.scenarioName || `#${touch.idCampaign || '—'}` }}</div>
                  <div v-if="touch.isEffectiveWinner || !touch.isTracked" class="sa-scenario-badges">
                    <span v-if="touch.isEffectiveWinner" class="sa-badge-winner">★ {{ t('saleAttribution.winner') }}</span>
                    <span v-if="!touch.isTracked" class="sa-badge-untracked">⚠ {{ t('saleAttribution.notTracked') }}</span>
                  </div>
                </td>
                <td>
                  <span class="sa-chip sa-chip-channel">{{ channelLabel(touch.channel) }}</span>
                </td>
                <td class="sa-cell-date">{{ formatCompactDate(touch.dateSend) }}</td>
                <td>
                  <div v-if="touch.dateLastClick" class="sa-interaction">
                    <span class="sa-tag sa-tag-click">{{ t('saleAttribution.click') }}</span>
                    {{ formatCompactDate(touch.dateLastClick) }}
                  </div>
                  <div v-else-if="touch.dateLastOpen" class="sa-interaction">
                    <span class="sa-tag sa-tag-open">{{ t('saleAttribution.open') }}</span>
                    {{ formatCompactDate(touch.dateLastOpen) }}
                  </div>
                  <div v-else class="tc-gray-400">—</div>
                </td>
                <td class="sa-lag">{{ lagSinceInteractionLabel(touch) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- FOOTER -->
        <div class="sa-footer">
          <div class="sa-footer-legend">
            <span class="sa-badge-winner">★</span> {{ t('saleAttribution.legend.winner') }}
            <span class="sa-footer-legend-sep">·</span>
            <span class="sa-badge-untracked">⚠</span> {{ t('saleAttribution.legend.notTracked') }}
          </div>
          <SpmButton
            :label="t('common.close')"
            class-style="p-button p-button-primary"
            @click="handleClose"
          />
        </div>
      </template>
    </div>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, PropType, computed, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import {
  AttributionTouch,
  getSaleAttributionDetail,
  SaleAttributionDetail,
} from '@/composables/stats/saleAttribution';

// Aligné sur libs/common/src/spmWorkflows/enums.ts → ChannelIntegerEnum.
const CHANNEL_LABELS: Record<number, string> = {
  1: 'Email',
  2: 'SMS',
  3: 'Popup',
  4: 'Smart Content',
  5: 'FB Messenger',
  6: 'Push',
  7: 'WhatsApp',
  8: 'RCS',
  9: 'Teaser',
};

const compactToSeconds = (compact: string | null): number | null => {
  if (!compact || compact === '0') return null;
  const padded = compact.padStart(14, '0');
  const y = Number(padded.slice(0, 4));
  const m = Number(padded.slice(4, 6)) - 1;
  const d = Number(padded.slice(6, 8));
  const h = Number(padded.slice(8, 10));
  const mi = Number(padded.slice(10, 12));
  const s = Number(padded.slice(12, 14));
  if (!y) return null;
  return Math.floor(Date.UTC(y, m, d, h, mi, s) / 1000);
};

const formatLagSeconds = (seconds: number): string => {
  const abs = Math.abs(seconds);
  if (abs < 60) return `${seconds} s`;
  if (abs < 3600) return `${Math.round(seconds / 60)} min`;
  if (abs < 86400) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86400).toFixed(1)} j`;
};

export default defineComponent({
  name: 'SaleAttributionModal',
  components: { Dialog, SpmButton },
  props: {
    modelValue: { type: Boolean, default: false },
    idShopOrder: { type: [String, Number] as PropType<string | number | null>, default: null },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const detail = ref<SaleAttributionDetail | null>(null);
    const loading = ref(false);

    // Fusionne touches propres + touches croisées de tous les groupes en une
    // liste unique, dédupliquée par (kind, idSend), triée globalement par
    // priorité d'attribution (winner DESC puis dernière interaction DESC).
    const allTouches = computed<AttributionTouch[]>(() => {
      if (!detail.value) return [];
      const merged = new Map<string, AttributionTouch>();
      for (const grp of detail.value.groups) {
        for (const touch of [...grp.touches, ...grp.crossChannelTouches]) {
          const key = `${touch.kind}:${touch.idSend}`;
          const existing = merged.get(key);
          if (!existing || (!existing.isWinner && touch.isWinner)) {
            merged.set(key, touch);
          }
        }
      }
      const lastInteractionTs = (touch: AttributionTouch): number => {
        const raw = touch.dateLastClick ?? touch.dateLastOpen ?? touch.dateSend;
        if (!raw) return 0;
        return Number(raw.padStart(14, '0'));
      };
      const sorted = Array.from(merged.values()).sort((a, b) => {
        if (a.isWinner !== b.isWinner) return a.isWinner ? -1 : 1;
        return lastInteractionTs(b) - lastInteractionTs(a);
      });
      const ranked = sorted.map((touch, idx) => ({ ...touch, rank: idx + 1 }));
      const persistedWinnerIsTracked = ranked.some((tt) => tt.isWinner && tt.isTracked);
      if (!persistedWinnerIsTracked) {
        const effectiveWinner = ranked.find((tt) => tt.isTracked);
        if (effectiveWinner) {
          return ranked.map((tt) => ({
            ...tt,
            isEffectiveWinner: tt.idSend === effectiveWinner.idSend && tt.kind === effectiveWinner.kind,
          }));
        }
      }
      return ranked.map((tt) => ({ ...tt, isEffectiveWinner: tt.isWinner }));
    });

    const totalAmount = computed(() => {
      if (!detail.value) return 0;
      return detail.value.groups.reduce((sum, g) => sum + g.amount, 0);
    });

    const currency = computed(() => {
      if (!detail.value || detail.value.groups.length === 0) return 'EUR';
      return detail.value.groups[0].currency || 'EUR';
    });

    const primarySaleDate = computed<string | null>(() => {
      if (!detail.value || detail.value.groups.length === 0) return null;
      return detail.value.groups[0].dateCreation || null;
    });

    const formattedSaleDate = computed(() => {
      if (!primarySaleDate.value) return '—';
      const padded = primarySaleDate.value.padStart(14, '0');
      return `${padded.slice(6, 8)}/${padded.slice(4, 6)}/${padded.slice(0, 4)} ${padded.slice(8, 10)}:${padded.slice(10, 12)}`;
    });

    const trackedCount = computed(() => allTouches.value.filter((tt) => tt.isTracked).length);
    const untrackedCount = computed(() => allTouches.value.length - trackedCount.value);
    const autoTouchesCount = computed(() => allTouches.value.filter((tt) => tt.kind === 'automation').length);
    const bulkTouchesCount = computed(() => allTouches.value.filter((tt) => tt.kind === 'bulk').length);

    const winnerLagDisplay = computed<string | null>(() => {
      const winner = allTouches.value.find((tt) => tt.isEffectiveWinner);
      if (!winner) return null;
      const saleSec = compactToSeconds(primarySaleDate.value);
      const interSec = compactToSeconds(winner.dateLastClick ?? winner.dateLastOpen);
      if (saleSec === null || interSec === null) return null;
      return formatLagSeconds(saleSec - interSec);
    });

    const formatAmount = (amount: number, ccy: string): string => {
      try {
        return new Intl.NumberFormat(locale.value, {
          style: 'currency',
          currency: ccy || 'EUR',
        }).format(amount);
      } catch (e) {
        return `${amount.toFixed(2)} ${ccy}`;
      }
    };

    const formatCompactDate = (raw: string | null): string => {
      if (!raw || raw === '0') return '—';
      const padded = raw.padStart(14, '0');
      return `${padded.slice(6, 8)}/${padded.slice(4, 6)} ${padded.slice(8, 10)}:${padded.slice(10, 12)}`;
    };

    const channelLabel = (channel: number | null): string => {
      if (channel === null) return '—';
      return CHANNEL_LABELS[channel] ?? `#${channel}`;
    };

    const lagSinceInteractionLabel = (touch: AttributionTouch): string => {
      const saleSec = compactToSeconds(primarySaleDate.value);
      const interSec = compactToSeconds(touch.dateLastClick ?? touch.dateLastOpen);
      if (saleSec === null || interSec === null) return '—';
      return formatLagSeconds(saleSec - interSec);
    };

    const fetch = async () => {
      if (!props.idShopOrder) {
        detail.value = null;
        return;
      }
      loading.value = true;
      try {
        detail.value = await getSaleAttributionDetail(props.idShopOrder);
      } finally {
        loading.value = false;
      }
    };

    watch(() => [props.modelValue, props.idShopOrder], async ([visible]) => {
      if (visible) {
        await fetch();
      } else {
        detail.value = null;
      }
    }, { immediate: true });

    const handleClose = () => {
      emit('update:modelValue', false);
    };

    return {
      t,
      detail,
      loading,
      allTouches,
      totalAmount,
      currency,
      formattedSaleDate,
      trackedCount,
      untrackedCount,
      autoTouchesCount,
      bulkTouchesCount,
      winnerLagDisplay,
      formatAmount,
      formatCompactDate,
      channelLabel,
      lagSinceInteractionLabel,
      handleClose,
    };
  },
});
</script>

<style scoped>
:deep(.sa-content) {
  padding: 0 !important;
}
.sa-modal {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  font-size: 13px;
  color: #212529;
  text-align: left;
}
.sa-header,
.sa-kpis,
.sa-kpi,
.sa-table-wrap,
.sa-footer { text-align: left; }
.sa-table td,
.sa-table th { text-align: left; }

/* Header */
.sa-header {
  padding: 22px 28px 18px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}
.sa-title {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 600;
  color: #212529;
}
.sa-subtitle {
  color: #6c757d;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.sa-subtitle strong { color: #3b3d3e; }
.sa-subtitle-sep { color: #ced4da; }
.sa-order-code {
  background: #f5f5f5;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 12px;
  color: #495057;
  font-family: ui-monospace, "SF Mono", Consolas, monospace;
}
.sa-close {
  background: none;
  border: none;
  font-size: 16px;
  color: #6c757d;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.sa-close:hover { background: #f5f5f5; color: #212529; }

/* States */
.sa-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  gap: 12px;
  color: #6c757d;
}
.sa-state-text { margin: 0; font-size: 14px; }

/* KPI grid */
.sa-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: #e9ecef;
  border-bottom: 1px solid #e9ecef;
}
.sa-kpi {
  background: #fff;
  padding: 18px 20px;
}
.sa-kpi-label {
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.sa-kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #212529;
  line-height: 1.1;
}
.sa-kpi-value--amount { color: #629d03; }
.sa-kpi-trend {
  font-size: 11px;
  color: #6c757d;
  margin-top: 4px;
}
.sa-kpi-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.sa-kpi-dot--primary { background: #94c840; }
.sa-kpi-dot--astronaut { background: #42526E; }
.sa-kpi-dot--orange { background: #fab710; }
.sa-kpi-dot--plum { background: #6f42c1; }

/* Table wrapper */
.sa-table-wrap {
  padding: 22px 28px 8px;
}
.sa-table-title {
  font-size: 13px;
  font-weight: 600;
  color: #3b3d3e;
  margin-bottom: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.sa-table-title-hint {
  font-size: 12px;
  color: #6c757d;
  font-weight: 400;
}

/* Table */
.sa-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.sa-table thead th {
  background: #f8f9fa;
  text-align: left;
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6c757d;
  border-bottom: 1px solid #e9ecef;
}
.sa-table tbody td {
  padding: 14px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: top;
}
.sa-table tbody tr:last-child td { border-bottom: none; }
.sa-table tbody tr.sa-row-winner {
  background: linear-gradient(to right, #e7f7ce 0%, rgba(231, 247, 206, 0.2) 100%);
}
.sa-table tbody tr.sa-row-winner td:first-child {
  border-left: 3px solid #94c840;
  padding-left: 11px;
}

/* Cells */
.sa-rank {
  font-weight: 700;
  color: #ced4da;
}
.sa-rank-top { color: #629d03; }
.sa-scenario {
  font-weight: 600;
  color: #212529;
}
.sa-scenario-badges {
  margin-top: 4px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.sa-cell-date { color: #495057; }
.sa-lag {
  font-weight: 600;
  color: #3b3d3e;
}

/* Chips */
.sa-chip {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}
.sa-chip-auto {
  background: #e6eaf3;
  color: #42526E;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.sa-chip-bulk {
  background: #ede5fa;
  color: #6f42c1;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.sa-chip-channel {
  background: #f5f5f5;
  color: #495057;
}

/* Badges */
.sa-badge-winner {
  background: #94c840;
  color: #fff;
  padding: 3px 9px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.sa-badge-untracked {
  background: #fab710;
  color: #fff;
  padding: 3px 9px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
}

/* Interaction tags */
.sa-interaction { font-size: 12px; color: #495057; }
.sa-tag {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 3px;
  margin-right: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.sa-tag-click { background: #e7f7ce; color: #629d03; }
.sa-tag-open { background: #e6eaf3; color: #42526E; }

/* Footer */
.sa-footer {
  padding: 14px 28px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.sa-footer-legend {
  font-size: 12px;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.sa-footer-legend-sep {
  color: #ced4da;
  margin: 0 4px;
}
</style>
